import React, { useEffect, useState } from 'react';

import { useTranslation } from 'next-i18next';
import PATHS from 'lib/navigation/paths';
import {
  AffiliateProgramDetails,
  AffiliateProgramForm,
} from 'views/information-module/AffiliateProgramView/common/components';

import StyledBox, {
  StyledInfoPageTitle,
} from 'views/information-module/AffiliateProgramView/AffiliateProgramView.styles';

import axios, { API } from 'lib/config/axios';
import useErrorsHandler from 'views/rebranded-module/common/hooks/useErrorsHandler';
import { useDispatch, useSelector } from 'react-redux';
import { setLoading, setSnackbar } from 'redux/features/ui-slice';
import ScrollTo from 'lib/utils/ScrollTo';
import generateMetaData from 'lib/utils/generateMetaData';
import Head from 'next/head';

const AffiliateProgramView = (props) => {
  // props
  const { ssrSeoValues } = props;
  // useStates
  const [seoValues, setSeoValues] = useState(ssrSeoValues);
  const [countries, setCountries] = useState([]);
  const [affiliateProgramForm, setAffiliateProgramForm] = useState({});
  const [affiliateProgramErrors, setAffiliateProgramErrors] = useState();
  const [selectedCountryStates, setSelectedCountryStates] = useState([]);

  // init
  const dispatch = useDispatch();
  const handleErrorResponse = useErrorsHandler();
  const appState = useSelector((state) => state.globalData.appState);

  const { t } = useTranslation('fe_er_affiliate_program_page');
  const breadcrumbsObject = {
    links: [
      {
        text: t('fe_er_affiliate_program_page:eagleRider'),
        path: PATHS.home(),
      },
      {
        text: t('fe_er_affiliate_program_page:information'),
        path: PATHS.information(),
      },
      {
        text: t('fe_er_affiliate_program_page:affiliate_Partner_program'),
      },
    ],
  };

  // handlers
  const getSeoValues = () => {
    axios
      .get(API.seoValues)
      .then((res) => {
        setSeoValues(res?.data);
      })
      .catch((error) => {
        handleErrorResponse(error, false);
      });
  };
  const getRegions = (targetCountry) => {
    dispatch(setLoading(true));
    axios
      .get(API.regions, {
        params: {
          country_id: targetCountry.id,
        },
      })
      .then((res) => {
        setSelectedCountryStates(res.data.regions);
        setAffiliateProgramForm((prev) => ({
          ...prev,
          state: res.data.regions[0].code,
        }));
      })
      .catch((error) => {
        handleErrorResponse(error);
      })
      .finally(() => dispatch(setLoading(false)));
  };
  const getCountries = () => {
    axios
      .get(API.countries.get)
      .then((res) => {
        setCountries(res.data.countries);
        setAffiliateProgramForm((prev) => ({
          ...prev,
          country: 'US',
          hasRegions: true,
        }));
        getRegions(res.data.countries.find((country) => country.iso2 === 'US'));
      })
      .catch((error) => {
        handleErrorResponse(error, false);
      });
  };

  const handleAffiliateProgramFormChange = (e) => {
    setAffiliateProgramForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };
  const handleRecaptchChange = (token) => {
    setAffiliateProgramForm((prev) => ({
      ...prev,
      'g-recaptcha-response': token,
    }));
  };

  const handleCountryChange = (e, newValue) => {
    setAffiliateProgramForm((prev) => ({
      ...prev,
      country: newValue.iso2,
      hasRegions: newValue.has_regions,
    }));
    if (newValue.has_regions) {
      getRegions(newValue);
    }
  };
  const handleStateChange = (e, newValue) => {
    setAffiliateProgramForm((prev) => ({
      ...prev,
      state: newValue.code,
    }));
  };

  const ResetAffiliateProgramForm = () => {
    setAffiliateProgramForm(() => ({
      country: 'US',
      hasRegions: true,
    }));
    getRegions(countries.find((country) => country.iso2 === 'US'));
    setAffiliateProgramErrors();
  };

  const handleSubmitForm = (recaptchaRef = null) => {
    dispatch(setLoading(true));
    const { 'g-recaptcha-response': recaptchaResponse, ...affiliateProgram } = affiliateProgramForm;
    axios
      .post(API.affiliateProgram, {
        authenticity_token: appState.authenticity_token,
        'g-recaptcha-response': recaptchaResponse,
        affiliate_program: affiliateProgram,
      })
      .then(() => {
        ScrollTo('affiliate-program-top');
        dispatch(
          setSnackbar({
            open: true,
            severity: 'success',
            message: t('fe_er_affiliate_program_page:thank_you_msg'),
          }),
        );
        ResetAffiliateProgramForm();
      })
      .catch((error) => {
        if (error.response.status == 406) {
          if (error.response.data.errors['g-recaptcha-response']) {
            dispatch(
              setSnackbar({
                open: true,
                severity: 'error',
                message: error.response.data.errors['g-recaptcha-response'],
              }),
            );
          }
          ScrollTo('join-now');
          setAffiliateProgramErrors(error.response.data);
        } else {
          handleErrorResponse(error, false);
        }
      })
      .finally(() => {
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
        dispatch(setLoading(false));
      });
  };

  // useEffects
  useEffect(() => {
    getCountries();
    if (ssrSeoValues) return;
    getSeoValues();
  }, []);

  return (
    <>
      {seoValues && <Head>{generateMetaData(seoValues)}</Head>}
      <StyledBox id="affiliate-program-top">
        <StyledInfoPageTitle
          mainTitleObject={{
            name: t('fe_er_affiliate_program_page:affiliate_Partner_program'),
          }}
          breadcrumbsObject={breadcrumbsObject}
          shareList={false}
          FloatingButtonWithFloatingDialogProp={false}
          buttonText={t('fe_er_affiliate_program_page:join_now')}
          handelJoinNowClick={() => ScrollTo('join-now')}
        />
        <AffiliateProgramDetails />
        <AffiliateProgramForm
          affiliateProgramForm={affiliateProgramForm}
          countries={countries}
          states={selectedCountryStates}
          hasRegions={affiliateProgramForm.hasRegions}
          handleCountryChange={handleCountryChange}
          handleStateChange={handleStateChange}
          handleSubmitForm={handleSubmitForm}
          handleAffiliateProgramFormChange={handleAffiliateProgramFormChange}
          handleRecaptchChange={handleRecaptchChange}
          affiliateProgramErrors={affiliateProgramErrors}
        />
      </StyledBox>
    </>
  );
};

export { AffiliateProgramView };
